import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store'
import { SliceType, setCompanyFields, setProductType } from '../../../store/businessSlice'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import styled from 'styled-components'
import Colors from '../../../assets/styles/Colors'
import Responsive from '../../../assets/config/Responsive'
import Slider from '@mui/material/Slider'
import FemaleIcon from '../../../assets/v2/JSX/FemaleIcon'
import MaleIcon from '../../../assets/v2/JSX/MaleIcon'
import Dropdown from '../../../components/v2/Dropdown'
import DatePicker from '../../../components/v2/DatePicker'
import TextArea from '../../../components/v2/TextArea'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import ProgressBar from '../../../components/v2/ProgressBar'
import WarningMessage from '../../../components/v2/WarningMessage'
import dayjs from 'dayjs'
import { parseISO } from 'date-fns'
import Checkbox from '../../../components/v2/buttons/Checkbox'
import Input from '../../../components/v2/Input'
import Title from '../../../components/v2/Title'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'

const HealthCompanyInfo = () => {
  const navigate = useNavigate()
  //
  const { t } = useTranslation()
  //
  const dispatch = useAppDispatch()
  //
  const { companyInfo } = useAppSelector((data: { business: SliceType }) => data.business)
  //
  const [percentage, setPercentage] = React.useState<number>(
    parseInt(companyInfo.genderBalance?.genderFemale ? companyInfo.genderBalance.genderFemale : '50'),
  )
  const malePercentage = 100 - percentage
  //
  const [insuredRecently, setInsuredRecently] = React.useState<boolean>(false)

  // DATA
  const pageData = [
    { id: 1, title: `${t('companyInfo')}`, url: `/health-company-info`, active: true, passed: true },
    { id: 2, title: `${t('contactPersonInfo')}`, url: '/company-contact-info', active: false, passed: false },
  ]
  //
  const headerCount = {
    passedCount: 1,
    totalCount: 2,
  }
  //
  //   dropdown array
  const insurancePayerArr = [
    {
      id: '1',
      label: `${t('company')}`,
      value: 'company',
    },
    {
      id: '2',
      label: `${t('employee')}`,
      value: 'employee',
    },
    {
      id: '3',
      label: `${t('both')}`,
      value: 'both',
    },
  ]
  //

  //   VALIDATION

  //
  // english and georgian letter with space and number regex
  const letterNumberRegex = /^[ა-ჰa-zA-Z0-9\s]*$/
  // english and georgian letter with space regex
  const letterSpaceRegex = /^[ა-ჰa-zA-Z\s]*$/
  // space regex
  const spaceRegex = /^\S*$/
  //

  const HealthInfoValidationSchema = Yup.object({
    companyName: Yup.string()
      .min(2, `${t('minTwoSymbolos')}`)
      .matches(letterNumberRegex, `${t('enterGeorgianOrEnglishLetters')}`)
      .required(`${t('required')}`),
    companyID: Yup.string()
      .min(9, `${t('minNineSymbolos')}`)
      .max(9, `${t('minNineSymbolos')}`)
      .matches(spaceRegex, `${t('spaceValidation')}`)
      .required(`${t('required')}`),
    companyActivity: Yup.string()
      .min(2, `${t('minTwoSymbolos')}`)
      .matches(letterSpaceRegex, `${t('enterGeorgianOrEnglishLetters')}`),
    employeeNumber: Yup.string().matches(spaceRegex, `${t('spaceValidation')}`),
    insuredEmployees: Yup.string().matches(spaceRegex, `${t('spaceValidation')}`),
    topManagers: Yup.string().matches(spaceRegex, `${t('spaceValidation')}`),
    employeeAverageAge: Yup.string().matches(spaceRegex, `${t('spaceValidation')}`),
    minPriceInsurance: Yup.string().matches(spaceRegex, `${t('spaceValidation')}`),
    insuranceCompanyName: Yup.string()
      .min(2, `${t('minTwoSymbolos')}`)
      .matches(letterNumberRegex, `${t('enterGeorgianOrEnglishLetters')}`),
    comment: Yup.string().min(2, `${t('minTwoSymbolos')}`),
  })

  const initialValues = {
    companyName: companyInfo.companyName || '',
    companyID: companyInfo.companyID || '',
    companyActivity: companyInfo.companyActivity || '',
    employeeNumber: companyInfo.employeeNumber || '',
    insuredEmployees: companyInfo.insuredEmployee || '',
    topManagers: companyInfo.managementNumber || '',
    employeeAverageAge: companyInfo.employeeAverageAge || '',
    minPriceInsurance: companyInfo.minPriceInsurance || '',
    insuranceCompanyName: companyInfo.insuredCompanyName || '',
    comment: companyInfo.comment || '',
  }

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: HealthInfoValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      console.log(values)
    },
  })

  //   VALIDATION

  // Button is valid
  const isValid = formik.isValid === true && companyInfo.companyName && companyInfo.companyID ? true : false

  const insuredAmountValid =
    companyInfo.companyName &&
    companyInfo.companyID &&
    companyInfo.employeeNumber &&
    companyInfo.insuredEmployee &&
    companyInfo.employeeNumber >= companyInfo.insuredEmployee

  const managementAmountValid =
    companyInfo.companyName &&
    companyInfo.companyID &&
    companyInfo.employeeNumber &&
    companyInfo.managementNumber &&
    companyInfo.employeeNumber >= companyInfo.managementNumber

  const amountValid = insuredAmountValid && managementAmountValid

  // Button is valid

  //   set product type for page and scroll to the top when rendering
  useEffect(() => {
    dispatch(setProductType('health'))
    window.scrollTo(0, 0)
  }, [dispatch])

  return (
    <Wrapper>
      <ScreenHeader totalCount={headerCount.totalCount} passedCount={headerCount.passedCount} />
      <Content>
        <Title text={t('companyInfo')} />
        <ProgressBar pageObj={pageData} />
        <WhiteBox>
          <Input
            id="companyName"
            label={t('companyName')}
            type="text"
            required={true}
            value={companyInfo.companyName || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'companyName',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('companyName', value),
            ]}
            width={'fullWidth'}
            error={formik.errors.companyName && companyInfo?.companyName ? true : false}
            errorText={formik.errors.companyName}
            onBlur={formik.handleBlur}
          />

          <Input
            id="company-id"
            label={t('companyID')}
            width={'fullWidth'}
            required={true}
            type="number"
            value={companyInfo.companyID || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'companyID',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('companyID', value),
            ]}
            error={formik.errors.companyID && companyInfo?.companyID ? true : false}
            errorText={formik.errors.companyID}
            onBlur={formik.handleBlur}
          />

          <Input
            id="company-field-of-activity"
            label={t('companyActivity')}
            type="text"
            width={'fullWidth'}
            value={companyInfo.companyActivity || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'companyActivity',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('companyActivity', value),
            ]}
            error={formik.errors.companyActivity && companyInfo?.companyActivity ? true : false}
            errorText={formik.errors.companyActivity}
            onBlur={formik.handleBlur}
          />

          <Input
            id="employee-number"
            label={t('employeeNumber')}
            width={'fullWidth'}
            type="text"
            value={companyInfo.employeeNumber || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'employeeNumber',
                  fieldValue: parseInt(value),
                }),
              ),
              formik.setFieldValue('employeeNumber', value),
            ]}
            error={formik.errors.employeeNumber && companyInfo?.employeeNumber ? true : false}
            errorText={formik.errors.employeeNumber}
            onBlur={formik.handleBlur}
          />

          <Input
            id="insured-employees"
            label={t('insuredEmployees')}
            width={'fullWidth'}
            type="text"
            value={companyInfo.insuredEmployee || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'insuredEmployee',
                  fieldValue: parseInt(value),
                }),
              ),
              formik.setFieldValue('insuredEmployees', value),
            ]}
            error={formik.errors.insuredEmployees && companyInfo?.insuredEmployee ? true : false}
            errorText={formik.errors.insuredEmployees}
            onBlur={formik.handleBlur}
          />

          {/* employee number validation */}
          {companyInfo.employeeNumber &&
          companyInfo.insuredEmployee &&
          companyInfo.insuredEmployee > companyInfo.employeeNumber ? (
            <WarningMessage variant="light-red" text={t('insuredMoreThanEmployee')} />
          ) : null}
          {/* employee number validation */}

          <Input
            id="top-managers"
            label={t('topManagement')}
            width={'fullWidth'}
            type="text"
            value={companyInfo.managementNumber || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'managementNumber',
                  fieldValue: parseInt(value),
                }),
              ),
              formik.setFieldValue('topManagers', value),
            ]}
            error={formik.errors.topManagers && companyInfo?.managementNumber ? true : false}
            errorText={formik.errors.topManagers}
            onBlur={formik.handleBlur}
          />

          {/* management number validation */}
          {companyInfo.employeeNumber &&
          companyInfo.managementNumber &&
          companyInfo.managementNumber > companyInfo.employeeNumber ? (
            <WarningMessage variant="light-red" text={t('managementMoreThaneEmployee')} />
          ) : null}
          {/* management number validation */}

          <Input
            id="average-age-employees"
            label={t('employeeAverageAge')}
            width={'fullWidth'}
            type="number"
            value={companyInfo.employeeAverageAge || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'employeeAverageAge',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('employeeAverageAge', value),
            ]}
            error={formik.errors.employeeAverageAge && companyInfo?.employeeAverageAge ? true : false}
            errorText={formik.errors.employeeAverageAge}
            onBlur={formik.handleBlur}
          />

          {/* Gender Balance Input */}
          <InputBox>
            <LabelStyle>{t('genderBalance')}</LabelStyle>
            <IconWrapper>
              <FemaleIcon />
              <span>
                {t('female')} {percentage}%{' '}
              </span>
            </IconWrapper>
            <SliderStyle
              aria-label="Gender Balance"
              valueLabelDisplay="on"
              min={0}
              max={100}
              $percentage={percentage}
              $malePercentage={malePercentage}
              defaultValue={
                parseInt(companyInfo?.genderBalance?.genderFemale ? companyInfo.genderBalance.genderFemale : '50') || 50
              }
              onChange={(_, value) => setPercentage(value as number)}
              sx={{
                padding: '9px 0px',
              }}
            />
            <IconWrapper>
              <MaleIcon />
              <span>
                {t('male')} {malePercentage}%{' '}
              </span>
            </IconWrapper>
          </InputBox>

          {/* Gender Balance Input */}

          <Line />

          <Input
            id="min-price-insurance"
            label={t('minPriceInsurance')}
            width={'fullWidth'}
            type="number"
            value={companyInfo.minPriceInsurance || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'minPriceInsurance',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('minPriceInsurance', value),
            ]}
            error={formik.errors.minPriceInsurance && companyInfo?.minPriceInsurance ? true : false}
            errorText={formik.errors.minPriceInsurance}
            onBlur={formik.handleBlur}
          />

          <Dropdown
            title={t('insurancePayer')}
            isSearchable={true}
            options={insurancePayerArr}
            value={companyInfo.insurancePayer || ''}
            onChange={(value) =>
              dispatch(
                setCompanyFields({
                  fieldName: 'insurancePayer',
                  fieldValue: value.value,
                }),
              )
            }
          />

          <Line />

          <Checkbox
            id="company-insured-recently"
            label={t('insuredRecently')}
            onClick={() => [
              setInsuredRecently(!insuredRecently),
              dispatch(
                setCompanyFields({
                  fieldName: 'insuredRecently',
                  fieldValue: !insuredRecently,
                }),
              ),
            ]}
            defaultChecked={companyInfo.insuredRecently || false}
          />
          {companyInfo?.insuredRecently && (
            <>
              <Input
                id="insurance-company-name"
                label={t('insuredCompanyName')}
                width={'fullWidth'}
                type="text"
                value={companyInfo.insuredCompanyName || ''}
                onChange={(value) => [
                  dispatch(
                    setCompanyFields({
                      fieldName: 'insuredCompanyName',
                      fieldValue: value,
                    }),
                  ),
                  formik.setFieldValue('insuranceCompanyName', value),
                ]}
                error={formik.errors.insuranceCompanyName && companyInfo?.insuredCompanyName ? true : false}
                errorText={formik.errors.insuranceCompanyName}
                onBlur={formik.handleBlur}
              />
              <DatePicker
                label={t('contractRenewalDate')}
                dateFormat="dd MMMM yyyy"
                className="start-date-class"
                selectedDate={companyInfo.contractRenewal ? parseISO(companyInfo.contractRenewal as string) : null}
                onchange={(value) =>
                  dispatch(
                    setCompanyFields({
                      fieldName: 'contractRenewal',
                      fieldValue: dayjs(value).format('YYYY-MM-DD'),
                    }),
                  )
                }
              />
            </>
          )}

          <Line />

          <TextArea
            id="add-comment"
            label={t('commentAdd')}
            width={'fullWidth'}
            value={companyInfo.comment || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'comment',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('comment', value),
            ]}
            error={formik.errors.comment && companyInfo?.comment ? true : false}
            errorText={formik.errors.comment}
            onBlur={formik.handleBlur}
          />
          {/* Common Fields */}
        </WhiteBox>

        <ButtonBox>
          <NavigationButton buttonType="prev" text={t('back')} onClick={() => navigate('/business-category')} />
          <NavigationButton
            buttonType="next"
            text={t('next')}
            onClick={() => [
              navigate('/company-contact-info'),
              dispatch(
                setCompanyFields({
                  fieldName: 'genderBalance',
                  fieldValue: {
                    genderMale: `${malePercentage}%`,
                    genderFemale: `${percentage}%`,
                  },
                }),
              ),
            ]}
            disabled={
              companyInfo.employeeNumber && companyInfo.managementNumber && companyInfo.insuredEmployee
                ? !amountValid
                : companyInfo.employeeNumber && companyInfo.insuredEmployee
                ? !insuredAmountValid
                : companyInfo.employeeNumber && companyInfo.managementNumber
                ? !managementAmountValid
                : !isValid
            }
          />
        </ButtonBox>
      </Content>

      <BackgroundIcon/>
    </Wrapper>
  )
}
export default HealthCompanyInfo

const Wrapper = styled.div`
  min-height: 100vh;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
  /*  */
`

const Content = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  position: relative;
  margin-top: 58px;
  z-index: 2;
  padding-inline: 20px;

  ${Responsive.mobile} {
    margin-top: 26px;
  }
  
`

const WhiteBox = styled.div`
  margin-top: 32px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background-color: ${Colors.white};
  width: 464px;
  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  padding: 32px 48px;
  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker-popper {
    position: unset !important;
    transform: unset !important;
    margin-top: 10px;
  }
  .react-datepicker {
    display: flex;
    justify-content: center;
  }
  ${Responsive.mobile} {
    margin-top: 26px;
    padding: 24px 20px;
    max-width: 350px;
    width: 100%;
  }
`
const ButtonBox = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 40px;
  button{
    width: 174px;
  }
  
  ${Responsive.mobile}{
    button{
      width: 140px;
    }
  }
`

const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${Colors.secondaryGrey};
`

const InputBox = styled.div`
  display: grid;
  row-gap: 4px;
  column-gap: 8px;
  width: 100%;
  grid-template-columns: 110px 140px 110px;
  grid-template-rows: 20px 1fr;
  align-items: flex-end;

  ${Responsive.mobile} {
    grid-template-columns: 110px 72px 110px;
  }
`
const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  padding-inline: 9px;

  span {
    font-size: 14px;
    color: ${Colors.primaryBlack};
  }
`

const LabelStyle = styled.label`
  grid-column-end: span 4;
  align-self: flex-start;
  width: inherit;
  font-size: 14px;
  line-height: 16px;
  margin-bottom: 5px;
  text-align: left;
  padding-right: 25px;
  color: ${Colors.primaryGrey};
`

const SliderStyle = styled(Slider)<{ $percentage: number; $malePercentage: number }>`
  .Mui-focusVisible {
    box-shadow: unset !important;
  }
  .MuiSlider-track {
    border: none;
    background-color: ${Colors.primaryBlack};
    height: 2px;
  }
  .MuiSlider-thumb {
    width: 10px;
    height: 10px;
    background-color: white;
    border: 2px solid black;
    &:focus,
    &:hover,
    &:active,
    ::before,
    ::after,
    .Mui-active {
      box-shadow: unset;
    }
  }
  .MuiSlider-rail {
    border: none;
    background-color: ${Colors.primaryBlack};
    opacity: 100%;
    border-radius: 0%;
    height: 2px;
  }
  .MuiSlider-valueLabel {
    color: red;
    width: 2px;
    height: 40px;
    background-color: ${Colors.Geyser};
    font-size: 0px;
    padding: 0;
    top: -2px;
    border-radius: 0%;
    ::before {
      position: absolute;
      content: '${(props) => props.$percentage + '%'}';
      top: 0;
      left: ${({ $percentage }) => ($percentage < 10 ? '-19px' : $percentage < 100 ? '-25px' : '-32px')};
      font-size: 12px;
      color: ${Colors.nevadaGrey};
      transform: unset;
      width: min-content;
      height: min-content;
      background-color: transparent;
    }
    ::after {
      position: absolute;
      content: '${(props) => props.$malePercentage + '%'}';
      top: 0;
      left: 6px;
      font-size: 12px;
      background-color: transparent;
      color: ${Colors.nevadaGrey};
      width: min-content;
    }
  }
`
